import http from '../../shared/http.js';
import { withFlash } from '../../shared/flash.js';
import { formModalSaved, isFormModalOpen } from '../../shared/formModal.js';
import { bindFormModule } from '../../shared/formBinding.js';
import { hideLoading, notifyError, notifySuccess, showLoading } from '../../shared/notify.js';

// Turns the metadata form into the JSON body the API expects.
function readForm(form) {
  const data = Object.fromEntries(new FormData(form).entries());

  // The role checkboxes repeat one name, so FormData.entries() keeps only the
  // last. getAll is what reads the whole group - the reason the field is a
  // checkbox group rather than a <select multiple> is that every option stays
  // visible and a stray click cannot drop the selection.
  data.sichtbarFuerRollen = new FormData(form).getAll('sichtbarFuerRollen');

  // A disabled select submits nothing, which is exactly right on an edit: the
  // area cannot change, and sending it would only be dropped server-side.
  if (!data.entitaet) delete data.entitaet;

  if (!data.beschreibung) data.beschreibung = null;

  // A new list starts on its entity's standard columns. Without this the model
  // would refuse it for having none, and asking somebody to pick columns before
  // they have seen the list is the wrong order to do the work in.
  const aus = leseAus(form);
  if (!form.dataset.listeId) {
    if (aus.spalten) data.spalten = aus.spalten.split(',').filter(Boolean);
    if (aus.filter) data.filter = sicherParsen(aus.filter);
  }

  return data;
}

// The query carried across from a filtered area page by "Neue Liste aus
// Filtern", so saving a list you have already narrowed does not start empty.
function leseAus(form) {
  return sicherParsen(form.dataset.aus) || {};
}

function sicherParsen(text) {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

async function submit(form) {
  const submitButton = form.querySelector('button[type="submit"]');
  const { listeId } = form.dataset;
  const data = readForm(form);

  if (submitButton) submitButton.disabled = true;
  showLoading('Wird gespeichert …');

  try {
    const antwort = listeId
      ? await http.patch(`/listenansichten/${listeId}`, data)
      : await http.post('/listenansichten', data);

    const neueId = antwort.data?.data?.listenansicht?._id;

    if (isFormModalOpen()) {
      hideLoading();
      formModalSaved();
      notifySuccess(listeId ? 'Liste wurde aktualisiert.' : 'Liste wurde erstellt.');

      // A newly created list opens straight away: its columns and filter are
      // built on its own page, so landing back on the index would just mean a
      // second click to get there.
      if (!listeId && neueId) window.location.href = `/listen/${neueId}`;
      return;
    }

    if (!listeId && neueId) {
      window.location.href = `/listen/${neueId}`;
      return;
    }

    window.location.href = withFlash('/listen', 'liste-aktualisiert');
  } catch (err) {
    hideLoading();
    if (submitButton) submitButton.disabled = false;
    notifyError(
      err.response?.data?.message || 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.'
    );
  }
}

// The role picker belongs to `sichtbarkeit: 'rolle'` and to nothing else.
//
// bindFormModule's third argument rather than a one-off listener, because it
// has to re-run for every form the dialog delivers - which is exactly what that
// parameter is for.
function setup(form) {
  const sichtbarkeit = form.querySelector('#sichtbarkeit');
  const rollenFeld = form.querySelector('#rollen-feld');
  if (!sichtbarkeit || !rollenFeld) return;

  const anwenden = () => {
    rollenFeld.hidden = sichtbarkeit.value !== 'rolle';
  };

  sichtbarkeit.addEventListener('change', anwenden);
  anwenden();
}

export function initListeForm() {
  bindFormModule('liste-form', submit, setup);
}
